import { useQuery } from "@tanstack/react-query";
import api from "../../services/api";

// Types for gift card data
export interface GiftCard {
  id: string;
  brand: string;
  amount: string;
  point_cost: number;
  is_active: number;
  created_at: string;
}

export interface GiftCardCatalogProps {
  onSelect: (giftCard: GiftCard) => void;
  selectedId?: string | null;
}

// Brand color configuration
const BRAND_CONFIG: Record<string, { color: string; bgColor: string; borderColor: string; label: string }> = {
  apple: {
    color: "text-gray-100",
    bgColor: "bg-gradient-to-br from-gray-800 to-gray-900",
    borderColor: "border-gray-600",
    label: "Apple",
  },
  google: {
    color: "text-blue-100",
    bgColor: "bg-gradient-to-br from-blue-600 to-blue-800",
    borderColor: "border-blue-400",
    label: "Google Play",
  },
  amazon: {
    color: "text-orange-100",
    bgColor: "bg-gradient-to-br from-orange-500 to-yellow-600",
    borderColor: "border-orange-300",
    label: "Amazon",
  },
};

// Fetch gift cards from API
async function fetchGiftCards(): Promise<GiftCard[]> {
  const response = await api.get<{ success: boolean; data: { giftCards: GiftCard[] } }>("/gift-cards");
  return response.data.data.giftCards;
}

// Gift card item component
interface GiftCardItemProps {
  giftCard: GiftCard;
  isSelected: boolean;
  onSelect: () => void;
}

function GiftCardItem({ giftCard, isSelected, onSelect }: GiftCardItemProps) {
  const brandConfig = BRAND_CONFIG[giftCard.brand] || {
    color: "text-gray-100",
    bgColor: "bg-gradient-to-br from-gray-600 to-gray-800",
    borderColor: "border-gray-500",
    label: giftCard.brand.charAt(0).toUpperCase() + giftCard.brand.slice(1),
  };

  return (
    <button
      onClick={onSelect}
      className={`
        relative w-full p-4 rounded-xl border-2 transition-all
        ${brandConfig.bgColor}
        ${isSelected ? "border-primary-400 ring-2 ring-primary-500/50" : brandConfig.borderColor}
        hover:scale-[1.02] active:scale-[0.98]
        text-left
      `}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}

      {/* Brand and amount */}
      <div className={`${brandConfig.color}`}>
        <p className="text-sm font-medium opacity-80">{brandConfig.label}</p>
        <p className="text-2xl font-bold mt-1">${giftCard.amount}</p>
      </div>

      {/* Point cost */}
      <div className="mt-3 pt-3 border-t border-white/20">
        <p className="text-sm font-semibold text-white/90">
          {giftCard.point_cost.toLocaleString()} points
        </p>
      </div>
    </button>
  );
}

// Group gift cards by brand
function groupByBrand(giftCards: GiftCard[]): Record<string, GiftCard[]> {
  return giftCards.reduce((acc, card) => {
    const brand = card.brand;
    if (!acc[brand]) {
      acc[brand] = [];
    }
    acc[brand].push(card);
    return acc;
  }, {} as Record<string, GiftCard[]>);
}

// Main component
export function GiftCardCatalog({ onSelect, selectedId }: GiftCardCatalogProps) {
  const { data: giftCards, isLoading, error } = useQuery({
    queryKey: ["gift-cards"],
    queryFn: fetchGiftCards,
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mb-4"></div>
        <p className="text-gray-400">Loading gift cards...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card bg-red-500/10 border-red-500/20">
        <div className="flex items-center gap-3 text-red-400">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium">Failed to load gift cards</p>
            <p className="text-sm text-gray-400">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!giftCards || giftCards.length === 0) {
    return (
      <div className="card text-center py-12">
        <div className="text-4xl mb-4">🎁</div>
        <p className="text-gray-400 mb-2">No gift cards available</p>
        <p className="text-gray-500 text-sm">Check back later for available rewards.</p>
      </div>
    );
  }

  // Group cards by brand
  const groupedCards = groupByBrand(giftCards);
  const brands = Object.keys(groupedCards).sort();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-100 mb-2">Redeem Your Points</h2>
        <p className="text-gray-400">Select a gift card to redeem your earned points</p>
      </div>

      {/* Gift cards by brand */}
      {brands.map((brand) => (
        <div key={brand}>
          {/* Brand header */}
          <h3 className="text-lg font-semibold text-gray-300 mb-3 flex items-center gap-2">
            {(BRAND_CONFIG[brand]?.label || brand).toUpperCase()}
            <span className="text-sm font-normal text-gray-500">
              ({groupedCards[brand].length} options)
            </span>
          </h3>

          {/* Cards grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {groupedCards[brand]
              .sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount))
              .map((giftCard) => (
                <GiftCardItem
                  key={giftCard.id}
                  giftCard={giftCard}
                  isSelected={selectedId === giftCard.id}
                  onSelect={() => onSelect(giftCard)}
                />
              ))}
          </div>
        </div>
      ))}

      {/* Footer info */}
      <div className="text-center text-sm text-gray-500 pt-4 border-t border-dark-border">
        <p>Gift cards are delivered electronically to your email within 24 hours.</p>
      </div>
    </div>
  );
}

export default GiftCardCatalog;
